import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ClipboardList, Save, Target, Plus, Trash2, ListChecks
} from 'lucide-react';
import api from '../services/api';
import './PlanCreate.css';

function PlanCreate() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation(['plans', 'common']);
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [periods, setPeriods] = useState([]);

  const currentYear = new Date().getFullYear();

  const [formData, setFormData] = useState({
    title: '',
    plan_type: 'monthly',
    department: '',
    period: '',
    year: currentYear,
    start_date: '',
    end_date: '',
    description: '',
  });

  const [tasks, setTasks] = useState([]);

  useEffect(() => {
    fetchReferenceData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchReferenceData = async () => {
    try {
      setLoading(true);
      const [deptsRes, prioRes, periodsRes] = await Promise.all([
        api.getDepartments(),
        api.getPriorities(),
        api.getPeriods(),
      ]);
      setDepartments(deptsRes.data.results || deptsRes.data);
      setPriorities(prioRes.data.results || prioRes.data);
      setPeriods(periodsRes.data.results || periodsRes.data);

      if (isEdit) {
        const planRes = await api.getPlan(id);
        const plan = planRes.data;
        setFormData({
          title: plan.title || '',
          plan_type: plan.plan_type || 'monthly',
          department: plan.department ? String(plan.department) : '',
          period: plan.period ? String(plan.period) : '',
          year: plan.year || currentYear,
          start_date: plan.start_date || '',
          end_date: plan.end_date || '',
          description: plan.description || '',
        });
        if (plan.tasks && plan.tasks.length > 0) {
          setTasks(plan.tasks.map(t => ({
            task_number: t.task_number || '',
            title: t.title || '',
            description: t.description || '',
            responsible_department: t.responsible_department ? String(t.responsible_department) : '',
            responsible_person: '',
            priority: t.priority ? String(t.priority) : '',
            start_date: t.start_date || '',
            deadline: t.deadline || '',
            expected_result: t.expected_result || '',
            required_resources: t.required_resources || '',
            status: t.status || 'not_started',
          })));
        }
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching reference data:', error);
      setLoading(false);
    }
  };

  const addTask = () => {
    setTasks([...tasks, {
      task_number: String(tasks.length + 1),
      title: '',
      description: '',
      responsible_department: formData.department || '',
      responsible_person: '',
      priority: '',
      start_date: formData.start_date || '',
      deadline: formData.end_date || '',
      expected_result: '',
      required_resources: '',
      status: 'not_started',
    }]);
  };

  const removeTask = (index) => {
    setTasks(tasks.filter((_, i) => i !== index));
  };

  const updateTask = (index, field, value) => {
    const updated = [...tasks];
    updated[index] = { ...updated[index], [field]: value };
    setTasks(updated);
  };

  const handleSave = async () => {
    if (!formData.title || !formData.department || !formData.start_date || !formData.end_date) {
      alert(t('plans:form.requiredFieldsAlert'));
      return;
    }

    setSaving(true);
    try {
      const payload = {
        title: formData.title,
        plan_type: formData.plan_type,
        department: parseInt(formData.department),
        period: formData.period ? parseInt(formData.period) : null,
        year: parseInt(formData.year),
        start_date: formData.start_date,
        end_date: formData.end_date,
        description: formData.description,
        tasks: tasks.filter(t => t.title.trim()).map(t => ({
          task_number: t.task_number,
          title: t.title,
          description: t.description,
          responsible_department: parseInt(t.responsible_department) || parseInt(formData.department),
          responsible_person: t.responsible_person ? parseInt(t.responsible_person) : null,
          priority: t.priority ? parseInt(t.priority) : null,
          start_date: t.start_date || null,
          deadline: t.deadline || formData.end_date,
          expected_result: t.expected_result,
          required_resources: t.required_resources,
          status: t.status || 'not_started',
        })),
      };

      if (isEdit) {
        await api.updatePlan(id, payload);
        alert(t('plans:form.planUpdated'));
      } else {
        payload.status = 'draft';
        await api.createPlan(payload);
        alert(t('plans:form.planCreated'));
      }

      navigate('/plans');
    } catch (error) {
      console.error('Error saving plan:', error);
      const detail = error.response?.data;
      alert(t('common:errors.generic') + ': ' + (typeof detail === 'string' ? detail : JSON.stringify(detail)));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="plan-create-page">
        <div className="loading-spinner">{t('common:loading')}</div>
      </div>
    );
  }

  return (
    <div className="plan-create-page">
      {/* Header */}
      <div className="plan-create-header">
        <div className="plan-create-header-left">
          <h1>{isEdit ? t('plans:pageTitles.editPlan') : t('plans:pageTitles.createPlan')}</h1>
        </div>
      </div>

      {/* Main info card */}
      <div className="form-card">
        <h2>
          <ClipboardList size={20} />
          {t('plans:form.mainInfo')}
        </h2>

        <div className="form-group">
          <label>{t('plans:form.planTitleRequired')}</label>
          <input
            type="text"
            className="form-control"
            placeholder={t('plans:form.planTitleCreatePlaceholder')}
            value={formData.title}
            onChange={e => setFormData({ ...formData, title: e.target.value })}
          />
        </div>

        <div className="form-row-3">
          <div className="form-group">
            <label>{t('plans:form.typeRequired')}</label>
            <select
              className="form-control"
              value={formData.plan_type}
              onChange={e => setFormData({ ...formData, plan_type: e.target.value })}
            >
              <option value="monthly">{t('plans:typeLabels.monthly')}</option>
              <option value="quarterly">{t('plans:typeLabels.quarterly')}</option>
              <option value="annual">{t('plans:typeLabels.annual')}</option>
            </select>
          </div>
          <div className="form-group">
            <label>{t('plans:form.departmentRequired')}</label>
            <select
              className="form-control"
              value={formData.department}
              onChange={e => setFormData({ ...formData, department: e.target.value })}
            >
              <option value="">{t('plans:form.selectDepartment')}</option>
              {departments.map(d => (
                <option key={d.id} value={d.id}>{d.name_ru}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>{t('plans:form.yearRequired')}</label>
            <select
              className="form-control"
              value={formData.year}
              onChange={e => setFormData({ ...formData, year: e.target.value })}
            >
              {[currentYear - 1, currentYear, currentYear + 1].map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-row-3">
          <div className="form-group">
            <label>{t('plans:form.startDateRequired')}</label>
            <input
              type="date"
              className="form-control"
              value={formData.start_date}
              onChange={e => setFormData({ ...formData, start_date: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>{t('plans:form.endDateRequired')}</label>
            <input
              type="date"
              className="form-control"
              value={formData.end_date}
              onChange={e => setFormData({ ...formData, end_date: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>{t('plans:form.reportPeriod')}</label>
            <select
              className="form-control"
              value={formData.period}
              onChange={e => setFormData({ ...formData, period: e.target.value })}
            >
              <option value="">{t('plans:form.notLinked')}</option>
              {periods.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label>{t('plans:form.description')}</label>
          <textarea
            className="form-control"
            rows="3"
            placeholder={t('plans:form.descriptionPlaceholder')}
            value={formData.description}
            onChange={e => setFormData({ ...formData, description: e.target.value })}
          />
        </div>
      </div>

      {/* Tasks card */}
      <div className="form-card">
        <div className="card-header-row">
          <h2>
            <Target size={20} />
            {t('plans:tasks.titleWithCount', { count: tasks.length })}
          </h2>
          <button className="btn-add" onClick={addTask}>
            <Plus size={16} />
            {t('plans:tasks.addTask')}
          </button>
        </div>

        {tasks.length === 0 ? (
          <div className="empty-hint">
            <ListChecks size={32} style={{ opacity: 0.3 }} />
            <p>{t('plans:tasks.addTaskHint')}</p>
          </div>
        ) : (
          <div className="tasks-form-list">
            {tasks.map((task, index) => (
              <div key={index} className="task-form-card">
                <div className="task-form-header">
                  <h3>{t('plans:tasks.taskNumber', { number: task.task_number })}</h3>
                  <button className="btn-remove" onClick={() => removeTask(index)}>
                    <Trash2 size={16} />
                  </button>
                </div>

                <div className="form-row">
                  <div className="form-group" style={{ maxWidth: 80 }}>
                    <label>{t('plans:tasks.number')}</label>
                    <input
                      type="text"
                      className="form-control"
                      value={task.task_number}
                      onChange={e => updateTask(index, 'task_number', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('plans:tasks.taskTitleRequired')}</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder={t('plans:tasks.taskTitlePlaceholder')}
                      value={task.title}
                      onChange={e => updateTask(index, 'title', e.target.value)}
                    />
                  </div>
                </div>

                <div className="form-row-3">
                  <div className="form-group">
                    <label>{t('plans:tasks.responsibleDepartment')}</label>
                    <select
                      className="form-control"
                      value={task.responsible_department}
                      onChange={e => updateTask(index, 'responsible_department', e.target.value)}
                    >
                      <option value="">{t('plans:form.selectDepartment')}</option>
                      {departments.map(d => (
                        <option key={d.id} value={d.id}>{d.name_ru}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>{t('plans:tasks.priority')}</label>
                    <select
                      className="form-control"
                      value={task.priority}
                      onChange={e => updateTask(index, 'priority', e.target.value)}
                    >
                      <option value="">{t('plans:tasks.priorityNotSet')}</option>
                      {priorities.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>{t('plans:tasks.deadlineRequired')}</label>
                    <input
                      type="date"
                      className="form-control"
                      value={task.deadline}
                      onChange={e => updateTask(index, 'deadline', e.target.value)}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>{t('plans:tasks.description')}</label>
                  <textarea
                    className="form-control"
                    rows="2"
                    placeholder={t('plans:tasks.descriptionPlaceholder')}
                    value={task.description}
                    onChange={e => updateTask(index, 'description', e.target.value)}
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>{t('plans:tasks.expectedResult')}</label>
                    <textarea
                      className="form-control"
                      rows="2"
                      placeholder={t('plans:tasks.expectedResultPlaceholder')}
                      value={task.expected_result}
                      onChange={e => updateTask(index, 'expected_result', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('plans:tasks.requiredResources')}</label>
                    <textarea
                      className="form-control"
                      rows="2"
                      placeholder={t('plans:tasks.requiredResourcesPlaceholder')}
                      value={task.required_resources}
                      onChange={e => updateTask(index, 'required_resources', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="form-card">
        <div className="form-actions">
          <button className="btn-secondary" onClick={() => navigate('/plans')}>
            {t('common:buttons.cancel')}
          </button>
          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            <Save size={18} />
            {saving ? t('common:loading') : (isEdit ? t('common:buttons.save') : t('plans:actions.createPlan'))}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PlanCreate;
